import React, { useContext, useEffect, useRef, useState } from "react";
import i18next from "i18next";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { makeStyles } from "tss-react/mui";
import { useHistory } from "react-router-dom";
import ROUTES, { getUrlWithAccountId } from "config/Routes";
import useSelectedAccountId from "components/common/hooks/useSelectedAccountId";
import { getAccountThirdpartyStates } from "services/edge/AccountService";
import { getAccountThirdparties } from "services/edge/ThirdpartyService";
import { getDossiersCountByType, getAccountDossiersSearch } from "services/edge/DossierService";
import { formatThirdpartyIdentifier } from "services/common/Utils";
import { Context } from "states/Store";
import { isAbortError } from "services/common/ApiUtils";
import { formatDt } from "./utils/lightUtils";
import LightStatusChip from "./components/LightStatusChip";
import { useAuth } from "@ea-webapp/auth";
import { RoleBasedAccessControlContext, PrivilegeEnum } from "services/common/RolesUtils";
import { BRAND, DOSSIER_STATUS, MEDIA } from "./lightTheme";

// ─── Chart segments ──────────────────────────────────────────────────────────
const SEGMENTS = [
  { key: "nbValid",             statusCode: "VALID",             get label() { return i18next.t("light.status.VALID"); },             color: "#15803D" },
  { key: "nbAbsent",            statusCode: "MISSING",           get label() { return i18next.t("light.status.MISSING"); },           color: "#9CA3AF" },
  { key: "nbCtrlInProgress",    statusCode: "IN_PROGRESS",       get label() { return i18next.t("light.status.IN_PROGRESS"); },       color: "#1D4ED8" },
  { key: "nbBelowExpectation",  statusCode: "BELOW_EXPECTATION", get label() { return i18next.t("light.status.BELOW_EXPECTATION"); }, color: "#6D00CC" },
  { key: "nbActionDORequired",  statusCode: "ACTION_REQUIRED",   get label() { return i18next.t("light.status.ACTION_REQUIRED"); },   color: "#EA580C" },
  { key: "nbInvalid",           statusCode: "NOT_VALID",         get label() { return i18next.t("light.status.NOT_VALID"); },         color: "#B91C1C" },
];


// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  root: {
    width: "100%", boxSizing: "border-box", flex: 1,
    display: "flex", flexDirection: "column", alignItems: "center",
    fontFamily: "Inter, sans-serif",
  },

  // ── Hero banner ──
  heroBanner: {
    width: "100%",
    background: `linear-gradient(135deg, ${BRAND.primary} 0%, #0a5c35 100%)`,
    backgroundImage: "url(https://www.aprovall.com/wp-content/uploads/2024/11/texture.webp)",
    backgroundSize: "cover", backgroundPosition: "center",
    padding: "3rem 2rem 2.5rem",
    display: "flex", flexDirection: "column", alignItems: "center",
    [MEDIA.smDown]: { padding: "2rem 1rem 2rem" },
  },
  heroGreeting: {
    fontSize: "clamp(1.4rem, 1rem + 1.5vw, 1.8rem)",
    fontWeight: 800, color: BRAND.white,
    fontFamily: "Inter, sans-serif", lineHeight: 1.2, textAlign: "center",
  },
  heroSubtitle: {
    fontSize: "0.88rem", color: "rgba(255,255,255,0.85)", fontWeight: 500,
    fontFamily: "Inter, sans-serif", textAlign: "center", marginTop: "0.35rem",
  },

  // ── Content ──
  content: {
    width: "100%", maxWidth: "1440px", padding: "0 2rem",
    boxSizing: "border-box", marginTop: "-1.5rem",
    display: "flex", flexDirection: "column", gap: "1.5rem",
    paddingBottom: "3rem",
    [MEDIA.smDown]: { padding: "0 1rem", gap: "1rem", paddingBottom: "2rem" },
  },

  // ── CTA row (overlapping hero) ──
  ctaRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "1rem",
    "@media (max-width: 640px)": { gridTemplateColumns: "1fr" },
  },
  ctaCard: {
    borderRadius: "14px", padding: "1.25rem 1.5rem",
    display: "flex", alignItems: "center", gap: "1rem",
    cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
    "&:hover": { transform: "translateY(-2px)" },
  },
  ctaPrimary: {
    backgroundColor: BRAND.white, color: BRAND.textMain,
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    border: `2px solid ${BRAND.secondary}`,
    "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.16)" },
  },
  ctaSecondary: {
    backgroundColor: BRAND.white, color: BRAND.textMain,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: `1.5px solid ${BRAND.border}`,
    "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.12)", borderColor: BRAND.primary },
  },
  ctaIconWrap: {
    width: "42px", height: "42px", borderRadius: "12px",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  ctaTitle: {
    fontSize: "0.92rem", fontWeight: 700, fontFamily: "Inter, sans-serif",
    color: BRAND.textMain, lineHeight: 1.2,
  },
  ctaDesc: {
    fontSize: "0.72rem", color: BRAND.textMuted, fontFamily: "Inter, sans-serif", marginTop: "0.1rem",
  },
  ctaArrow: {
    marginLeft: "auto", color: BRAND.primary, flexShrink: 0, opacity: 0.5,
    transition: "opacity 0.15s, transform 0.15s",
    "$ctaCard:hover &": { opacity: 1, transform: "translateX(2px)" },
  },

  // ── KPI strip ──
  kpiStrip: {
    display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem",
    [MEDIA.tablet]:  { gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem" },
    [MEDIA.mobile]:  { gridTemplateColumns: "1fr", gap: "0.75rem" },
  },
  kpiCard: {
    borderRadius: "14px", padding: "1.1rem 1.3rem",
    backgroundColor: BRAND.white, border: `1px solid ${BRAND.border}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    display: "flex", alignItems: "center", gap: "0.85rem",
  },
  kpiIcon: {
    width: "38px", height: "38px", borderRadius: "10px",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  kpiValue: {
    fontSize: "1.5rem", fontWeight: 800, color: BRAND.textMain,
    fontFamily: "Inter, sans-serif", lineHeight: 1, letterSpacing: "-0.02em",
  },
  kpiLabel: {
    fontSize: "0.68rem", color: BRAND.textMuted, fontFamily: "Inter, sans-serif", marginTop: "0.1rem",
  },

  // ── Two-column grid ──
  twoCol: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem",
    "@media (max-width: 720px)": { gridTemplateColumns: "1fr" },
  },

  // ── Section card ──
  sectionCard: {
    backgroundColor: BRAND.white, borderRadius: "14px",
    border: `1px solid ${BRAND.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    overflow: "hidden", display: "flex", flexDirection: "column",
  },
  sectionHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0.85rem 1.25rem", borderBottom: `1px solid ${BRAND.border}`,
  },
  sectionTitle: {
    fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.08em", color: BRAND.textMuted, fontFamily: "Inter, sans-serif",
  },
  sectionLink: {
    fontSize: "0.72rem", color: BRAND.primary, fontWeight: 600,
    fontFamily: "Inter, sans-serif", cursor: "pointer",
    display: "flex", alignItems: "center", gap: "0.2rem",
    "&:hover": { textDecoration: "underline" },
  },

  // ── List row ──
  listRow: {
    display: "flex", alignItems: "center", gap: "0.75rem",
    padding: "0.75rem 1.25rem", borderBottom: `1px solid ${BRAND.border}`,
    cursor: "pointer", transition: "background 0.1s",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: BRAND.background },
  },
  listIcon: {
    width: "32px", height: "32px", borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  listInfo: {
    flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "0.1rem",
  },
  listName: {
    fontSize: "0.82rem", fontWeight: 600, color: BRAND.textMain,
    fontFamily: "Inter, sans-serif", whiteSpace: "nowrap",
    overflow: "hidden", textOverflow: "ellipsis",
  },
  listMeta: {
    fontSize: "0.68rem", color: BRAND.textMuted, fontFamily: "Inter, sans-serif",
    display: "flex", alignItems: "center", gap: "0.3rem",
  },
  statusChip: {
    display: "inline-flex", alignItems: "center",
    padding: "2px 8px", borderRadius: "20px",
    fontSize: "0.62rem", fontWeight: 600,
    fontFamily: "Inter, sans-serif", lineHeight: "16px", flexShrink: 0,
  },
  emptyText: {
    padding: "1.5rem 1.25rem", fontSize: "0.78rem",
    color: BRAND.textMuted, fontFamily: "Inter, sans-serif",
    fontStyle: "italic", textAlign: "center",
  },

  // ── Chart ──
  chartCard: {
    backgroundColor: BRAND.white, borderRadius: "14px",
    border: `1px solid ${BRAND.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  chartRow: {
    display: "flex", alignItems: "center", gap: "0.75rem",
    padding: "0.4rem 1.25rem", cursor: "pointer",
    transition: "background 0.1s",
    "&:hover": { backgroundColor: BRAND.background },
    [MEDIA.smDown]: { padding: "0.4rem 0.9rem", gap: "0.5rem" },
  },
  chartLabel: {
    width: "220px", flexShrink: 0, fontSize: "0.75rem",
    color: BRAND.textMain, fontFamily: "Inter, sans-serif",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    [MEDIA.smDown]: { width: "100px" },
  },
  chartBarWrap: {
    flex: 1, height: "14px", borderRadius: "4px", marginRight: "0.75rem",
    overflow: "hidden", backgroundColor: BRAND.background, display: "flex",
  },
  chartSegment: {
    cursor: "pointer",
    transition: "filter 0.18s ease, opacity 0.18s ease",
    "&:hover": { filter: "brightness(1.25) saturate(1.1)", opacity: 0.88, zIndex: 1 },
  },
  chartTotal: {
    width: "30px", flexShrink: 0, fontSize: "0.7rem", fontWeight: 700,
    color: BRAND.textMuted, fontFamily: "Inter, sans-serif", textAlign: "right",
  },
  chartLegend: {
    display: "flex", flexWrap: "wrap", gap: "0.4rem 1rem",
    padding: "0.6rem 1.25rem 0.75rem", borderTop: `1px solid ${BRAND.border}`,
  },
  legendItem: {
    display: "flex", alignItems: "center", gap: "0.3rem",
    fontSize: "0.65rem", color: BRAND.textMuted, fontFamily: "Inter, sans-serif",
  },
  legendDot: { width: "7px", height: "7px", borderRadius: "2px", flexShrink: 0 },
}));

// ─── DossierChart ────────────────────────────────────────────────────────────
function DossierChart({ byType, totalDossiers, isLoading, onTypeClick, onSegmentClick, classes: s }) {
  const rows = (byType ?? [])
    .map((t) => ({
      label: t.typeDossierLibelle || i18next.t("light.common.freeEvaluation"),
      code: t.typeDossierCode,
      total: t.nbTotal ?? SEGMENTS.reduce((sum, seg) => sum + (t[seg.key] ?? 0), 0),
      counts: SEGMENTS.map((seg) => ({ ...seg, count: t[seg.key] ?? 0 })),
    }))
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total);

  const activeSegments = SEGMENTS.filter((seg) =>
    (byType ?? []).some((t) => (t[seg.key] ?? 0) > 0)
  );

  return (
    <Box className={s.chartCard}>
      <Box className={s.sectionHead}>
        <Typography className={s.sectionTitle}>{i18next.t("light.home.chartTitle")}</Typography>
        {!isLoading && totalDossiers != null && (
          <Typography style={{ fontSize: "0.7rem", color: BRAND.textMuted, fontFamily: "Inter" }}>{totalDossiers} {i18next.t("light.home.chartTotal")}</Typography>
        )}
      </Box>
      <Box style={{ padding: "0.4rem 0" }}>
        {isLoading ? (
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "1rem 1.25rem" }}>
            <CircularProgress size={14} style={{ color: BRAND.primary }} />
            <Typography style={{ fontSize: "0.75rem", color: BRAND.textMuted, fontFamily: "Inter" }}>{i18next.t("light.common.loading")}</Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography className={s.emptyText}>{i18next.t("light.home.noActiveEval")}</Typography>
        ) : rows.map((row) => (
          <Box key={row.code} className={s.chartRow} onClick={() => onTypeClick(row.code)}>
            <Typography className={s.chartLabel} title={row.label}>{row.label}</Typography>
            <Box className={s.chartBarWrap}>
              {row.counts.filter((c) => c.count > 0).map((c) => (
                <Tooltip key={c.key} title={`${c.label} : ${c.count}`} placement="top" disableInteractive>
                  <Box
                    className={s.chartSegment}
                    onClick={(e) => { e.stopPropagation(); onSegmentClick(row.code, c.statusCode); }}
                    style={{ width: `${(c.count / row.total) * 100}%`, backgroundColor: c.color }}
                  />
                </Tooltip>
              ))}
            </Box>
            <Typography className={s.chartTotal}>{row.total}</Typography>
          </Box>
        ))}
      </Box>
      {!isLoading && activeSegments.length > 0 && (
        <Box className={s.chartLegend}>
          {activeSegments.map((seg) => (
            <Box key={seg.key} className={s.legendItem}>
              <Box className={s.legendDot} style={{ backgroundColor: seg.color }} />
              {seg.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── LightHome ────────────────────────────────────────────────────────────────
export default function LightHome() {
  const { classes: s, cx } = useStyles();
  const { authState } = useAuth();
  const history = useHistory();
  const accountId = useSelectedAccountId();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const canCreateDossier = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER);

  const [tpTotal, setTpTotal]               = useState(null);
  const [tpLoading, setTpLoading]           = useState(true);
  const [dossierByType, setDossierByType]   = useState(null);
  const [totalDossiers, setTotalDossiers]   = useState(null);
  const [chartLoading, setChartLoading]     = useState(true);
  const [recentDossiers, setRecentDossiers] = useState([]);
  const [recentLoading, setRecentLoading]   = useState(true);
  const [recentTPs, setRecentTPs]           = useState([]);
  const [recentTPsLoading, setRecentTPsLoading] = useState(true);

  const abortRef = useRef();
  const firstName = authState.user?.given_name || "";

  useEffect(() => {
    if (!accountId || !state.account) return;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    // TP count
    (async () => {
      try {
        const res = await getAccountThirdpartyStates(accountId, {}, null, signal);
        if (res?.content) setTpTotal(res.content.reduce((sum, d) => sum + (d.count ?? 0), 0));
      } catch (e) { if (!isAbortError(e)) console.error(e); }
      finally { setTpLoading(false); }
    })();

    // Chart by type
    (async () => {
      try {
        const res = await getDossiersCountByType(accountId, null, signal);
        if (res?.content) {
          setDossierByType(res.content.countDossierType ?? []);
          setTotalDossiers(res.content.numberOfDossiers ?? null);
        }
      } catch (e) { if (!isAbortError(e)) console.error(e); }
      finally { setChartLoading(false); }
    })();

    // Last 3 evaluations
    (async () => {
      try {
        const res = await getAccountDossiersSearch(accountId, true, null, { page: 0, rowPerPage: 3 }, {}, [{ field: "creationDate", sort: "desc" }], dispatch);
        setRecentDossiers(res?.content ?? []);
      } catch (e) { if (!isAbortError(e)) console.error(e); }
      finally { setRecentLoading(false); }
    })();

    // Last 3 thirdparties added
    (async () => {
      try {
        const res = await getAccountThirdparties(accountId, true, { page: 0, rowPerPage: 3 }, {}, [{ field: "followingDate", sort: "desc" }], dispatch, signal);
        setRecentTPs(res?.content ?? []);
      } catch (e) { if (!isAbortError(e)) console.error(e); }
      finally { setRecentTPsLoading(false); }
    })();

    return () => abortRef.current?.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, state.account]);

  const handleTypeClick = (code) => {
    history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_EVALUATIONS), { dossierType: code });
  };
  const handleSegmentClick = (typeCode, statusCode) => {
    history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_EVALUATIONS), { dossierType: typeCode, statusCode });
  };

  return (
    <Box className={s.root}>
      {/* ── Hero ── */}
      <Box className={s.heroBanner}>
        <Typography className={s.heroGreeting}>
          {i18next.t("light.home.hello")}{firstName ? `, ${firstName}` : ""}
        </Typography>
        <Typography className={s.heroSubtitle}>{i18next.t("light.home.subtitle")}</Typography>
      </Box>

      <Box className={s.content}>

        {/* ── CTAs ── */}
        <Box className={s.ctaRow}>
          {canCreateDossier && (
          <Box
            className={cx(s.ctaCard, s.ctaPrimary)}
            onClick={() => history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_LAUNCH))}
          >
            <Box className={s.ctaIconWrap} style={{ backgroundColor: `${BRAND.secondary}30` }}>
              <RocketLaunchOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
            </Box>
            <Box style={{ flex: 1 }}>
              <Typography className={s.ctaTitle}>{i18next.t("light.home.launchTitle")}</Typography>
              <Typography className={s.ctaDesc}>{i18next.t("light.home.launchDesc")}</Typography>
            </Box>
            <ArrowForwardIcon className={s.ctaArrow} style={{ fontSize: "18px" }} />
          </Box>
          )}

          {(state.contextModels ?? []).map((m) => (
            <Box
              key={m.id}
              className={cx(s.ctaCard, s.ctaSecondary)}
              onClick={() => history.push(`${getUrlWithAccountId(accountId, ROUTES.LIGHT_CONTEXTS)}?modelId=${m.id}`)}
            >
              <Box className={s.ctaIconWrap} style={{ backgroundColor: `${BRAND.primary}10` }}>
                <WorkspacesOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography className={s.ctaTitle} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</Typography>
                <Typography className={s.ctaDesc}>{i18next.t("light.home.contextDesc")}</Typography>
              </Box>
              <ArrowForwardIcon className={s.ctaArrow} style={{ fontSize: "18px" }} />
            </Box>
          ))}

          <Box
            className={cx(s.ctaCard, s.ctaSecondary)}
            onClick={() => history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_THIRDPARTIES))}
          >
            <Box className={s.ctaIconWrap} style={{ backgroundColor: BRAND.background }}>
              <BusinessOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
            </Box>
            <Box style={{ flex: 1 }}>
              <Typography className={s.ctaTitle}>{i18next.t("light.home.tpTitle")}</Typography>
              <Typography className={s.ctaDesc}>{i18next.t("light.home.tpDesc")}</Typography>
            </Box>
            <ArrowForwardIcon className={s.ctaArrow} style={{ fontSize: "18px" }} />
          </Box>
        </Box>

        {/* ── KPI strip ── */}
        <Box className={s.kpiStrip}>
          <Box className={s.kpiCard}>
            <Box className={s.kpiIcon} style={{ backgroundColor: `${BRAND.primary}10` }}>
              <BusinessOutlinedIcon style={{ fontSize: "18px", color: BRAND.primary }} />
            </Box>
            <Box>
              {tpLoading
                ? <CircularProgress size={14} style={{ color: BRAND.secondary }} />
                : <Typography className={s.kpiValue}>{tpTotal ?? "–"}</Typography>
              }
              <Typography className={s.kpiLabel}>{i18next.t("light.home.kpiFollowed")}</Typography>
            </Box>
          </Box>

          <Box className={s.kpiCard}>
            <Box className={s.kpiIcon} style={{ backgroundColor: "#1D4ED810" }}>
              <FolderOutlinedIcon style={{ fontSize: "18px", color: "#1D4ED8" }} />
            </Box>
            <Box>
              {chartLoading
                ? <CircularProgress size={14} style={{ color: BRAND.secondary }} />
                : <Typography className={s.kpiValue}>{totalDossiers ?? "–"}</Typography>
              }
              <Typography className={s.kpiLabel}>{i18next.t("light.home.kpiActive")}</Typography>
            </Box>
          </Box>

          <Box className={s.kpiCard}>
            <Box className={s.kpiIcon} style={{ backgroundColor: "#15803D10" }}>
              <TrendingUpIcon style={{ fontSize: "18px", color: "#15803D" }} />
            </Box>
            <Box>
              {chartLoading
                ? <CircularProgress size={14} style={{ color: BRAND.secondary }} />
                : <Typography className={s.kpiValue}>
                    {dossierByType ? dossierByType.reduce((sum, t) => sum + (t.nbValid ?? 0), 0) : "–"}
                  </Typography>
              }
              <Typography className={s.kpiLabel}>{i18next.t("light.home.kpiCompliant")}</Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Two columns: Recent evals + Recent TPs ── */}
        <Box className={s.twoCol}>
          {/* Recent evaluations */}
          <Box className={s.sectionCard}>
            <Box className={s.sectionHead}>
              <Typography className={s.sectionTitle}>{i18next.t("light.home.lastEvals")}</Typography>
              <Typography className={s.sectionLink} onClick={() => history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_THIRDPARTIES))}>
                {i18next.t("light.home.seeAll")} <ArrowForwardIcon style={{ fontSize: "12px" }} />
              </Typography>
            </Box>
            {recentLoading ? (
              <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "1rem 1.25rem" }}>
                <CircularProgress size={14} style={{ color: BRAND.primary }} />
              </Box>
            ) : recentDossiers.length === 0 ? (
              <Typography className={s.emptyText}>{i18next.t("light.home.noEval")}</Typography>
            ) : recentDossiers.map((d) => {
              const cfg = DOSSIER_STATUS[d.statusCode] ?? { color: BRAND.textMuted, bg: BRAND.background, label: d.statusCode };
              return (
                <Box key={d.dossierId} component="a" href={getUrlWithAccountId(accountId, `light/dossiers/${d.dossierId}`)} className={s.listRow} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); history.push(getUrlWithAccountId(accountId, `light/dossiers/${d.dossierId}`)); }} style={{ textDecoration: "none", color: "inherit" }}>
                  <Box className={s.listIcon} style={{ backgroundColor: `${cfg.color}15` }}>
                    <FolderOutlinedIcon style={{ fontSize: "15px", color: cfg.color }} />
                  </Box>
                  <Box className={s.listInfo}>
                    <Typography className={s.listName}>{d.dossierTypeLibelle || i18next.t("light.common.freeEvaluation")}</Typography>
                    <Typography className={s.listMeta}>
                      {d.thirdpartyName || "–"} · {formatDt(d.creationDate)}
                    </Typography>
                  </Box>
                  <LightStatusChip code={d.statusCode} className={s.statusChip} />
                </Box>
              );
            })}
          </Box>

          {/* Recent thirdparties */}
          <Box className={s.sectionCard}>
            <Box className={s.sectionHead}>
              <Typography className={s.sectionTitle}>{i18next.t("light.home.newTp")}</Typography>
              <Typography className={s.sectionLink} onClick={() => history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_THIRDPARTIES))}>
                {i18next.t("light.home.seeAll")} <ArrowForwardIcon style={{ fontSize: "12px" }} />
              </Typography>
            </Box>
            {recentTPsLoading ? (
              <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "1rem 1.25rem" }}>
                <CircularProgress size={14} style={{ color: BRAND.primary }} />
              </Box>
            ) : recentTPs.length === 0 ? (
              <Typography className={s.emptyText}>{i18next.t("light.home.noTp")}</Typography>
            ) : recentTPs.map((tp) => (
              <Box key={tp.thirdpartyId} component="a" href={getUrlWithAccountId(accountId, `light/thirdparties/${tp.thirdpartyId}`)} className={s.listRow} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); history.push(getUrlWithAccountId(accountId, `light/thirdparties/${tp.thirdpartyId}`)); }} style={{ textDecoration: "none", color: "inherit" }}>
                <Box className={s.listIcon} style={{ backgroundColor: BRAND.background }}>
                  <BusinessOutlinedIcon style={{ fontSize: "15px", color: BRAND.primary }} />
                </Box>
                <Box className={s.listInfo}>
                  <Typography className={s.listName}>{tp.name}</Typography>
                  <Typography className={s.listMeta}>
                    {formatThirdpartyIdentifier(tp) || tp.addressCity || "–"} · {formatDt(tp.followingDate)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Chart ── */}
        <DossierChart
          byType={dossierByType}
          totalDossiers={totalDossiers}
          isLoading={chartLoading}
          onTypeClick={handleTypeClick}
          onSegmentClick={handleSegmentClick}
          classes={s}
        />

      </Box>
    </Box>
  );
}
